import { useEffect, useRef, useState } from "react";

/**
 * Custom hook that scrolls to an element and automatically highlights it
 * @param {Object} styles - CSS module styles object
 * @param {number} yOffset - Vertical offset for scrolling (default: 100)
 * @param {number} highlightDuration - How long to show the highlight in ms (default: 2000)
 * @returns {Object} - { scrollToElement }
 */
const useScrollAndHighlight = (
  styles,
  yOffset = 20,
  highlightDuration = 1000
) => {
  const timeoutRef = useRef(null);

  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // Clear any existing highlight timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Remove any existing highlight classes from all sections
      // Use the CSS module class name if available, otherwise fallback to string
      const highlightClass = styles?.scrollHighlight || "scrollHighlight";
      const allSections = document.querySelectorAll(`.${highlightClass}`);
      allSections.forEach((section) => {
        section.classList.remove(highlightClass);
      });

      // Scroll to element with offset
      const elementTop =
        element.getBoundingClientRect().top + window.pageYOffset;
      const targetPosition = elementTop - yOffset;

      // Store original scroll margin
      const originalScrollMargin = element.style.scrollMarginTop;

      // Set custom scroll margin to create offset (only user's offset, header is fixed)
      element.style.scrollMarginTop = `${yOffset}px`;

      // Use scrollIntoView which will respect the scroll margin
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Restore original scroll margin after scroll completes
      setTimeout(() => {
        element.style.scrollMarginTop = originalScrollMargin;
      }, 1000);

      // Highlight the element after scroll completes
      setTimeout(() => {
        element.classList.add(highlightClass);
        // Clear highlight after duration
        timeoutRef.current = setTimeout(() => {
          element.classList.remove(highlightClass);
        }, highlightDuration);
      }, 300); // Wait for scroll to complete
    } else {
      console.log("❌ Element not found with ID:", id);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { scrollToElement };
};

export default useScrollAndHighlight;
